import {
    Box,
    Text,
    Heading,
    Picture,
    Button,
} from "@smile2impress/components/relaunch";
import { STRAPI_WEBSITE_API_URL } from "api/cms-website/cms-website";

import Link from "components/Link";
import {
    extractTextFromReactBlockNode,
    ReactBlockNode,
} from "utils/extractTextFromStrapiBlockNode";
import { getImageUrl } from "utils/imageUrl";
import { kebabCase } from "utils/kebabCase";

export const blocks = {
    heading: ({
        children,
        level,
    }: {
        children?: React.ReactNode;
        level: 1 | 2 | 3 | 4 | 5 | 6;
    }) => {
        if (!children) {
            return null;
        }

        const childrenArray =
            extractTextFromReactBlockNode(children as ReactBlockNode[]) || "";
        const id = kebabCase(childrenArray);

        switch (level) {
            case 1:
                return (
                    <Heading
                        variant="h1"
                        id={id}
                        fontSize="26px"
                        marginBottom="20px"
                        marginTop="40px"
                        fontWeight="600"
                    >
                        {" "}
                        {children}
                    </Heading>
                );
            case 2:
                return (
                    <Heading
                        variant="h2"
                        id={id}
                        fontSize="20px"
                        marginBottom="14px"
                        marginTop="28px"
                        fontWeight="600"
                    >
                        {children}
                    </Heading>
                );
            case 3:
                return (
                    <Heading
                        variant="h3"
                        id={id}
                        fontSize="16px"
                        marginBottom="14px"
                        marginTop="28px"
                        fontWeight="600"
                    >
                        {children}
                    </Heading>
                );
            case 4:
                return (
                    <Heading
                        variant="h4"
                        id={id}
                        fontSize="16px"
                        marginBottom="14px"
                        marginTop="28px"
                        fontWeight="600"
                    >
                        {children}
                    </Heading>
                );
            case 5:
                return (
                    <Heading
                        variant="h5"
                        id={id}
                        fontSize="16px"
                        marginBottom="14px"
                        marginTop="28px"
                        fontWeight="600"
                    >
                        {children}
                    </Heading>
                );
            case 6:
                return (
                    <Heading
                        variant="h6"
                        id={id}
                        fontSize="16px"
                        marginBottom="14px"
                        marginTop="28px"
                        fontWeight="600"
                    >
                        {children}
                    </Heading>
                );
            default:
                return (
                    <Heading
                        variant="h2"
                        id={id}
                        fontSize="20px"
                        marginBottom="14px"
                        marginTop="28px"
                        fontWeight="600"
                    >
                        {children}
                    </Heading>
                );
        }
    },
    paragraph: ({ children }: { children?: React.ReactNode }) =>
        children ? (
            <Text
                margin="15px 0"
                sx={{
                    "> b, strong": {
                        fontWeight: 700,
                    },
                }}
            >
                {children}
            </Text>
        ) : null,
    quote: ({ children }: { children?: React.ReactNode }) =>
        children ? (
            <Text
                margin="40px 0"
                fontWeight="700"
                lineHeight="1"
                fontSize="32px"
                color="#444"
                outline="none"
                _before={{
                    content: '""',
                    display: "block",
                    width: "30%",
                    height: "30px",
                    backgroundColor: "#ff5e58",
                    marginBottom: "30px",
                }}
                _after={{
                    content: '""',
                    display: "block",
                    width: "30%",
                    height: "30px",
                    backgroundColor: "#c8e6ef",
                    marginTop: "30px",
                }}
            >
                {children}
            </Text>
        ) : null,
    image: (props: any) => {
        const imageUrl = getImageUrl(props.image.url, STRAPI_WEBSITE_API_URL);
        return (
            <Box as="figure" margin="24px 0">
                <Picture
                    images={{
                        jpeg: imageUrl,
                        webp: imageUrl,
                    }}
                    defaultSrc={imageUrl}
                    style={{ maxWidth: "100%" }}
                />
            </Box>
        );
    },
    button: ({
        children,
        link,
    }: {
        children?: React.ReactNode;
        link: string;
    }) => {
        return (
            <Box textAlign="center">
                <Button
                    as={Link}
                    href={link}
                    variant="red"
                    size="lg"
                    margin="15px auto"
                >
                    {children}
                </Button>
            </Box>
        );
    },
    embed: ({
        children,
        link,
    }: {
        children?: React.ReactNode;
        link: string;
    }) => {
        const getEmbedUrl = (link: string) => {
            try {
                const url = new URL(link.trim());
                const host = url.hostname.replace(/^www\./, "");
                const pathSegments = url.pathname.split("/").filter(Boolean);

                let videoId: string | undefined;

                if (host === "youtu.be") {
                    videoId = pathSegments[0];
                } else if (host === "youtube.com" || host === "m.youtube.com") {
                    videoId = url.searchParams.get("v") ?? undefined;
                    if (
                        !videoId &&
                        (pathSegments[0] === "embed" ||
                            pathSegments[0] === "shorts")
                    ) {
                        videoId = pathSegments[1];
                    }
                }
                return videoId
                    ? `https://www.youtube.com/embed/${videoId}`
                    : link.trim();
            } catch {
                return link;
            }
        };

        return (
            <Box margin="0 0 15px 0">
                <Box
                    position="relative"
                    width="100%"
                    sx={{ aspectRatio: "16 / 9" }}
                >
                    <Box
                        as="iframe"
                        src={getEmbedUrl(link)}
                        position="absolute"
                        top={0}
                        left={0}
                        width="100%"
                        height="100%"
                        border={0}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        referrerPolicy="strict-origin-when-cross-origin"
                        allowFullScreen
                    />
                </Box>
                {children && <Text textStyle="body">{children}</Text>}
            </Box>
        );
    },
};
